import { request, requestBlob, saveBlob } from './core'
import type { JobStatus } from './types'

export interface DeliverableFile {
  name: string
  last_modified: string | null
  size: number
}

/** Lifecycle of the background PowerPoint generation worker. */
export interface PresentationStatus {
  status: JobStatus
  error?: string | null
  filename: string
}

export const generateApi = {
  /** STORED submission files only (signed sign-off, presentation deck, manual
   * uploads). assessment.docx and customer-sign-off.pdf are NOT listed — the
   * backend renders them fresh on every download. */
  listDeliverables: (slug: string) =>
    request<{ files: DeliverableFile[] }>(`/api/assessments/${slug}/deliverables`),

  /** Download a submission file (docx / pdf / pptx) and trigger a save.
   * assessment.docx and customer-sign-off.pdf are rendered fresh server-side
   * on each call. Goes through requestBlob so the bearer token is sent — a
   * plain <a href> can't carry it, so the API would just return a 401 JSON
   * body instead. Saved with the assessment slug prefixed
   * (acme-co-presentation.pptx) so files from different assessments don't
   * collide in Downloads. */
  downloadDeliverable: async (slug: string, filename: string): Promise<void> => {
    const blob = await requestBlob(
      `/api/assessments/${slug}/deliverables/${encodeURIComponent(filename)}`,
    )
    saveBlob(blob, `${slug}-${filename}`)
  },

  /** Delete a single stored final/ file (e.g. an upload added by mistake). */
  deleteDeliverable: (slug: string, filename: string) =>
    request<{ ok: boolean; deleted: boolean }>(
      `/api/assessments/${slug}/deliverables/${encodeURIComponent(filename)}`,
      { method: 'DELETE' },
    ),

  /** Hand-upload a file into final/ — it ships as a standalone submission
   *  file. `asSignoff` stores it as customer-sign-off-SIGNED.pdf — the manual
   *  signing path when DocuSign isn't available. */
  uploadDeliverable: (slug: string, file: globalThis.File, opts?: { asSignoff?: boolean }) => {
    const fd = new FormData()
    fd.append('file', file)
    if (opts?.asSignoff) fd.append('as_signoff', 'true')
    return request<{ ok: boolean; filename: string; as_signoff: boolean }>(
      `/api/assessments/${slug}/deliverables/upload`,
      { method: 'POST', body: fd },
    )
  },

  /** Kick off PowerPoint deck generation — a background worker that takes a
   *  few minutes. Poll getPresentation after. */
  generatePresentation: (slug: string, revision?: number) =>
    request<{ status: JobStatus; revision: number }>(
      `/api/assessments/${slug}/presentation`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  getPresentation: (slug: string) =>
    request<PresentationStatus>(`/api/assessments/${slug}/presentation`),
}
